import React, { useState, useEffect, useCallback } from "react";
import styles from "./Supervisor.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { emptySupervisorStreak, SupervisorStreak } from "../types/supervisor";

interface DashboardStats {
    total_students: number;
    avg_qbank_progress: number;
    avg_mock_score: number;
    recently_active: number;
}

interface Student {
    student_id: string;
    email: string;
    full_name: string;
    joined_at: string;
    last_active: string | null;
    qbank_reveals: number;
    qbank_total: number;
    qbank_progress_pct: number;
    mock_exams_taken: number;
    mock_avg_score: number;
    login_count: number;
    streak?: SupervisorStreak;
}

interface StudentsResponse {
    students: Student[];
    total: number;
    page: number;
    per_page: number;
}

type SortField = "full_name" | "qbank_progress_pct" | "current_streak" | "mock_avg_score" | "last_active";

interface Props {
    supervisorId: string;
    onStudentClick: (studentId: string) => void;
    onStudentRemoved: () => void;
}

type ReportPreset = "7" | "30" | "90" | "custom";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

const SupervisorDashboard: React.FC<Props> = ({ supervisorId, onStudentClick, onStudentRemoved }) => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [studentsData, setStudentsData] = useState<StudentsResponse | null>(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sortField, setSortField] = useState<SortField>("full_name");
    const [sortAsc, setSortAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    const [confirmRemove, setConfirmRemove] = useState<Student | null>(null);
    const [removing, setRemoving] = useState(false);
    const [showReport, setShowReport] = useState(false);

    const perPage = 20;

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, studentsRes] = await Promise.all([
                authenticatedFetch(`${apiUrl}/api/supervisor/dashboard?supervisor_id=${supervisorId}`),
                authenticatedFetch(`${apiUrl}/api/supervisor/students?supervisor_id=${supervisorId}&page=${page}&per_page=${perPage}`),
            ]);
            if (statsRes.ok) setStats(await statsRes.json());
            if (studentsRes.ok) setStudentsData(await studentsRes.json());
        } catch (err) {
            console.error("Failed to fetch dashboard data", err);
        } finally {
            setLoading(false);
        }
    }, [supervisorId, page]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(true);
        }
    };

    const handleRemoveStudent = async () => {
        if (!confirmRemove) return;
        setRemoving(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/supervisor/students/${confirmRemove.student_id}?supervisor_id=${supervisorId}`,
                { method: "DELETE" }
            );
            if (res.ok) {
                (window as any).showNotification?.("Student removed successfully", "success");
                setConfirmRemove(null);
                fetchDashboard();
                onStudentRemoved();
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed to remove student", "error");
            }
        } catch (err) {
            (window as any).showNotification?.("Failed to remove student", "error");
        } finally {
            setRemoving(false);
        }
    };

    const sortArrow = (field: SortField) => {
        if (sortField !== field) return "";
        return sortAsc ? " \u25B2" : " \u25BC";
    };

    // Filter + sort students
    let students = studentsData?.students || [];
    if (search.trim()) {
        const q = search.toLowerCase();
        students = students.filter(
            (s) => s.full_name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q)
        );
    }
    students = [...students].sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
            case "full_name":
                cmp = (a.full_name || a.email).localeCompare(b.full_name || b.email);
                break;
            case "qbank_progress_pct":
                cmp = a.qbank_progress_pct - b.qbank_progress_pct;
                break;
            case "current_streak":
                cmp = (a.streak ?? emptySupervisorStreak).current_days
                    - (b.streak ?? emptySupervisorStreak).current_days;
                break;
            case "mock_avg_score":
                cmp = a.mock_avg_score - b.mock_avg_score;
                break;
            case "last_active":
                cmp = (a.last_active || "").localeCompare(b.last_active || "");
                break;
        }
        return sortAsc ? cmp : -cmp;
    });

    const totalPages = studentsData ? Math.ceil(studentsData.total / perPage) : 1;

    const formatDate = (date: string | null) => {
        if (!date) return "Never";
        try {
            return new Date(date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return "Unknown";
        }
    };

    if (loading && !stats) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} />
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div>
            <div className={styles.dashboardActions}>
                <div>
                    <h1 className={styles.dashboardTitle}>Student overview</h1>
                    <p className={styles.dashboardSubtitle}>Monitor engagement and learning progress across your cohort.</p>
                </div>
                <button className={styles.primaryButton} onClick={() => setShowReport(true)}>
                    Export report
                </button>
            </div>
            {/* Stat Cards */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>
                        Active Students
                        <span className={styles.infoIcon} title="Total number of students currently linked to your account">i</span>
                    </div>
                    <div className={styles.statValue}>{stats?.total_students ?? 0}</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>
                        Avg QBank Progress
                        <span className={styles.infoIcon} title="Average percentage of published questions your students have revealed across the entire question bank">i</span>
                    </div>
                    <div className={styles.statValue}>
                        {stats?.avg_qbank_progress ?? 0}
                        <span className={styles.statSuffix}>%</span>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>
                        Avg Mock Score
                        <span className={styles.infoIcon} title="Average percentage score across completed mock exams for all students">i</span>
                    </div>
                    <div className={styles.statValue}>
                        {stats?.avg_mock_score ?? 0}
                        <span className={styles.statSuffix}>%</span>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>
                        Active This Week
                        <span className={styles.infoIcon} title="Number of students who logged in within the last 7 days">i</span>
                    </div>
                    <div className={styles.statValue}>{stats?.recently_active ?? 0}</div>
                </div>
            </div>

            {/* Student Table */}
            <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                    <span className={styles.tableTitle}>Students</span>
                    <input
                        className={styles.searchInput}
                        type="text"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {students.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>{search ? "No students match your search." : "No students linked yet. Send invitations to get started."}</p>
                    </div>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort("full_name")}>
                                        Name{sortArrow("full_name")}
                                    </th>
                                    <th>Email</th>
                                    <th onClick={() => handleSort("qbank_progress_pct")}>
                                        QBank Progress{sortArrow("qbank_progress_pct")}
                                    </th>
                                    <th onClick={() => handleSort("current_streak")}>
                                        Streak{sortArrow("current_streak")}
                                    </th>
                                    <th onClick={() => handleSort("mock_avg_score")}>
                                        Mock Avg{sortArrow("mock_avg_score")}
                                    </th>
                                    <th onClick={() => handleSort("last_active")}>
                                        Last Active{sortArrow("last_active")}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((student) => (
                                    <tr
                                        key={student.student_id}
                                        className={styles.tableRow}
                                        onClick={() => onStudentClick(student.student_id)}
                                    >
                                        <td>{student.full_name || "Unnamed"}</td>
                                        <td>{student.email}</td>
                                        <td>
                                            <div className={styles.progressBarContainer}>
                                                <div className={styles.progressBar}>
                                                    <div
                                                        className={styles.progressFill}
                                                        style={{ width: `${Math.min(student.qbank_progress_pct, 100)}%` }}
                                                    />
                                                </div>
                                                <span className={styles.progressText}>{student.qbank_progress_pct}%</span>
                                            </div>
                                        </td>
                                        <td>
                                            {(() => {
                                                const streak = student.streak ?? emptySupervisorStreak;
                                                const currentText = streak.current_days > 0
                                                    ? `${streak.current_days} day${streak.current_days === 1 ? "" : "s"}`
                                                    : streak.longest_days > 0 ? "No current streak" : "Not started";
                                                return (
                                                    <div className={styles.streakCell}>
                                                        <strong>{currentText}</strong>
                                                        <span>{streak.studied_today ? "Practiced today" : "Not yet today"}</span>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td>{student.mock_avg_score > 0 ? `${student.mock_avg_score}%` : "-"}</td>
                                        <td>{formatDate(student.last_active)}</td>
                                        <td>
                                            <button
                                                className={`${styles.dangerButton} ${styles.smallButton}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmRemove(student);
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    className={styles.paginationButton}
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    Previous
                                </button>
                                <span className={styles.paginationInfo}>
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    className={styles.paginationButton}
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Remove confirmation modal */}
            {confirmRemove && (
                <div className={styles.modalOverlay} onClick={() => setConfirmRemove(null)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3>Remove Student</h3>
                        <p>
                            Are you sure you want to remove <strong>{confirmRemove.full_name || confirmRemove.email}</strong>?
                            If they have a supervisor-granted subscription, it will be revoked.
                        </p>
                        <div className={styles.modalActions}>
                            <button className={styles.secondaryButton} onClick={() => setConfirmRemove(null)}>
                                Cancel
                            </button>
                            <button className={styles.dangerButton} onClick={handleRemoveStudent} disabled={removing}>
                                {removing ? "Removing..." : "Remove"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showReport && (
                <SupervisorReportModal
                    supervisorId={supervisorId}
                    onClose={() => setShowReport(false)}
                />
            )}
        </div>
    );
};

const toISODate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const SupervisorReportModal: React.FC<{ supervisorId: string; onClose: () => void }> = ({ supervisorId, onClose }) => {
    const today = new Date();
    const [preset, setPreset] = useState<ReportPreset>("30");
    const [startDate, setStartDate] = useState(() => {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return toISODate(start);
    });
    const [endDate, setEndDate] = useState(toISODate(today));
    const [scope, setScope] = useState<"all" | "selected">("all");
    const [detail, setDetail] = useState<"summary" | "detailed">("detailed");
    const [students, setStudents] = useState<Student[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [studentSearch, setStudentSearch] = useState("");
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (preset === "custom") return;
        const end = new Date();
        const start = new Date(end);
        start.setDate(start.getDate() - (Number(preset) - 1));
        setStartDate(toISODate(start));
        setEndDate(toISODate(end));
    }, [preset]);

    useEffect(() => {
        if (scope !== "selected" || students.length || loadingStudents) return;
        let cancelled = false;
        const load = async () => {
            setLoadingStudents(true);
            setError("");
            try {
                const loaded: Student[] = [];
                let page = 1;
                let total = 1;
                while (loaded.length < total) {
                    const response = await authenticatedFetch(`${apiUrl}/api/supervisor/students?supervisor_id=${supervisorId}&page=${page}&per_page=100`);
                    if (!response.ok) throw new Error("Unable to load students");
                    const data: StudentsResponse = await response.json();
                    loaded.push(...data.students);
                    total = data.total;
                    page += 1;
                }
                if (!cancelled) setStudents(loaded);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : "Unable to load students");
            } finally {
                if (!cancelled) setLoadingStudents(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [scope, students.length, loadingStudents, supervisorId]);

    const visibleStudents = students.filter((student) => {
        const query = studentSearch.trim().toLowerCase();
        return !query || student.full_name.toLowerCase().includes(query) || student.email.toLowerCase().includes(query);
    });

    const toggleStudent = (id: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const generate = async () => {
        setError("");
        if (!startDate || !endDate || startDate > endDate) {
            setError("Choose a valid date range.");
            return;
        }
        if (scope === "selected" && selectedIds.size === 0) {
            setError("Select at least one student.");
            return;
        }
        setGenerating(true);
        try {
            const response = await authenticatedFetch(`${apiUrl}/api/supervisor/reports/pdf`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    supervisor_id: supervisorId,
                    start_date: startDate,
                    end_date: endDate,
                    student_ids: scope === "selected" ? Array.from(selectedIds) : null,
                    detail_level: detail,
                }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || "Unable to generate the report");
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `c-shaped-supervisor-report-${startDate}-to-${endDate}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unable to generate the report");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onMouseDown={onClose} role="presentation">
            <div className={`${styles.modal} ${styles.reportModal}`} role="dialog" aria-modal="true" aria-labelledby="report-title" onMouseDown={(event) => event.stopPropagation()}>
                <div className={styles.reportHeader}>
                    <div>
                        <h3 id="report-title">Export activity report</h3>
                        <p>Create a professional PDF for your selected reporting period.</p>
                    </div>
                    <button className={styles.iconButton} onClick={onClose} aria-label="Close">×</button>
                </div>

                <fieldset className={styles.reportFieldset}>
                    <legend>Reporting period</legend>
                    <div className={styles.segmentedControl}>
                        {(["7", "30", "90", "custom"] as ReportPreset[]).map((value) => (
                            <button key={value} type="button" className={preset === value ? styles.segmentActive : ""} onClick={() => setPreset(value)}>
                                {value === "custom" ? "Custom" : `${value} days`}
                            </button>
                        ))}
                    </div>
                    {preset === "custom" && (
                        <div className={styles.dateGrid}>
                            <label>From<input type="date" value={startDate} max={endDate} onChange={(event) => setStartDate(event.target.value)} /></label>
                            <label>To<input type="date" value={endDate} min={startDate} max={toISODate(today)} onChange={(event) => setEndDate(event.target.value)} /></label>
                        </div>
                    )}
                </fieldset>

                <div className={styles.reportOptionsGrid}>
                    <label>Students<select value={scope} onChange={(event) => setScope(event.target.value as "all" | "selected")}><option value="all">All students</option><option value="selected">Selected students</option></select></label>
                    <label>Report detail<select value={detail} onChange={(event) => setDetail(event.target.value as "summary" | "detailed")}><option value="detailed">Summary + student details</option><option value="summary">Summary only</option></select></label>
                </div>

                {scope === "selected" && (
                    <div className={styles.studentPicker}>
                        <div className={styles.studentPickerHeader}><strong>Select students</strong><span>{selectedIds.size} selected</span></div>
                        <input type="search" placeholder="Search students…" value={studentSearch} onChange={(event) => setStudentSearch(event.target.value)} />
                        <div className={styles.studentPickerList}>
                            {loadingStudents ? <p>Loading students…</p> : visibleStudents.map((student) => (
                                <label key={student.student_id}>
                                    <input type="checkbox" checked={selectedIds.has(student.student_id)} onChange={() => toggleStudent(student.student_id)} />
                                    <span><strong>{student.full_name || "Unnamed student"}</strong><small>{student.email}</small></span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {error && <p className={styles.reportError} role="alert">{error}</p>}
                <div className={styles.modalActions}>
                    <button className={styles.secondaryButton} onClick={onClose} disabled={generating}>Cancel</button>
                    <button className={styles.primaryButton} onClick={generate} disabled={generating || loadingStudents}>{generating ? "Generating PDF…" : "Generate PDF"}</button>
                </div>
            </div>
        </div>
    );
};

export default SupervisorDashboard;
